import classNames from 'classnames';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info_outline_24.svg';
import { GIFT_MESSAGE_MAX_LENGTH, GiftOptionsErrors, GiftOptionsValue } from '../../lib/giftRecipient';
import { GiftShipsTo } from '../../types/gql-op-types';
import styles from './GiftOptions.module.scss';

interface GiftOptionsProps {
  value: GiftOptionsValue;
  errors: GiftOptionsErrors;
  onChange: (value: GiftOptionsValue) => void;
  /** an empty email is only an error once the shopper has left the field or tried to pay */
  showEmailError: boolean;
  onEmailBlur: () => void;
  /** the order holds more than the gifted trackers, all of it shipping to one address */
  mixed?: boolean;
}

const SHIP_OPTIONS = [
  { shipsTo: GiftShipsTo.RECIPIENT, labelKey: 'hardware_first_checkout_gift_ship_to_them' },
  { shipsTo: GiftShipsTo.PURCHASER, labelKey: 'hardware_first_checkout_gift_ship_to_me' },
];

export default function GiftOptions({
  value,
  errors,
  onChange,
  showEmailError,
  onEmailBlur,
  mixed = false,
}: GiftOptionsProps) {
  const { t } = useTranslation();
  const id = useId();
  const toThem = value.shipsTo === GiftShipsTo.RECIPIENT;
  const emailError = showEmailError && errors.email ? t(errors.email) : undefined;
  const messageError = errors.message ? t(errors.message, { max: GIFT_MESSAGE_MAX_LENGTH }) : undefined;

  return (
    <>
      <div className={classNames(styles.label, styles.shipLabel)} id={`${id}-ship`}>
        {t('hardware_first_checkout_gift_ship_label')}
      </div>
      <div aria-labelledby={`${id}-ship`} className={styles.fields} role="radiogroup">
        {SHIP_OPTIONS.map(({ shipsTo, labelKey }) => (
          <label className={classNames(styles.option, { [styles.on]: value.shipsTo === shipsTo })} key={shipsTo}>
            <input
              checked={value.shipsTo === shipsTo}
              className={styles.radioInput}
              name={`${id}-ship`}
              onChange={() => onChange({ ...value, shipsTo })}
              type="radio"
              value={shipsTo}
            />
            <span aria-hidden="true" className={styles.radio} />
            <span className={styles.optionName}>{t(labelKey)}</span>
          </label>
        ))}
      </div>
      <p className={styles.explain}>
        {t(toThem ? 'hardware_first_checkout_gift_to_them_explain' : 'hardware_first_checkout_gift_to_me_explain')}
      </p>
      {toThem && (
        <>
          <label className={styles.label} htmlFor={`${id}-email`}>
            {t('hardware_first_checkout_gift_recipient_email')}
          </label>
          <input
            aria-describedby={emailError ? `${id}-email-error` : undefined}
            aria-invalid={!!emailError}
            autoComplete="off"
            className={styles.input}
            id={`${id}-email`}
            onBlur={onEmailBlur}
            onChange={(event) => onChange({ ...value, email: event.currentTarget.value })}
            placeholder="name@email.com"
            required
            type="email"
            value={value.email}
          />
          {emailError && (
            <p className={styles.error} id={`${id}-email-error`}>
              {emailError}
            </p>
          )}
          <label className={styles.label} htmlFor={`${id}-message`}>
            {t('hardware_first_checkout_gift_message_label')}
            <em>{t('hardware_first_checkout_gift_message_optional')}</em>
          </label>
          <div className={styles.messageWrap}>
            <textarea
              aria-describedby={`${id}-message-hint`}
              aria-invalid={!!messageError}
              className={classNames(styles.input, styles.message)}
              id={`${id}-message`}
              maxLength={GIFT_MESSAGE_MAX_LENGTH}
              onChange={(event) => onChange({ ...value, message: event.currentTarget.value })}
              placeholder={t('hardware_first_checkout_gift_message_placeholder')}
              value={value.message}
            />
            <span aria-hidden="true" className={styles.count}>
              {t('hardware_first_checkout_gift_message_count', {
                count: value.message.length,
                max: GIFT_MESSAGE_MAX_LENGTH,
              })}
            </span>
          </div>
          {messageError && <p className={styles.error}>{messageError}</p>}
          <p className={styles.hint} id={`${id}-message-hint`}>
            {t('hardware_first_checkout_gift_message_hint')}
          </p>
        </>
      )}
      {mixed && (
        <p className={styles.note}>
          <InfoIcon />
          {t('hardware_first_checkout_gift_mixed')}
        </p>
      )}
    </>
  );
}
